import React, { useCallback, useEffect, useState } from "react";

import ChevronDownIcon from "@assets/icons/chevron-down.svg";
import CheckIcon from "@assets/icons/check.svg";

import { cn } from "@utils/tailwind.util";
import { IBaseTheme, IThemeOption } from "@interfaces/sourcing.interface";
import { ThemeBadge } from "./ThemeBadge";
import { Spinner } from "@ui/Loaders";
import { View } from "@ui/View";

export type IOptions = (
  | IThemeOption
  | { title: "No Sourcing Theme"; level: number; children: any[]; id: string }
)[];

interface IThemeTreeProps {
  options: IOptions;
  selected?: Map<string, number>;
  selectedBranches: IBaseTheme[];
  onSelect: (theme: IThemeOption) => void;
  selectedIds?: string[];
  expandAll?: boolean;
  loadingThemeIds?: Set<string>;
  isLevelOneBased?: boolean;
  isMaxLimitReached?: boolean;
  searchQuery?: string;
  className?: string;
}

export const ThemeTree = ({
  options,
  selected,
  selectedBranches,
  onSelect,
  selectedIds,
  expandAll = false,
  loadingThemeIds = new Set(),
  isLevelOneBased = false,
  isMaxLimitReached = false,
  searchQuery,
  className,
}: IThemeTreeProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const isSearchMode = searchQuery !== undefined;

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  }, []);

  const isNodeExpanded = (id: string) => expandAll || expandedIds.has(id);

  const handleItemKeyDown = (event: React.KeyboardEvent, item: IThemeOption) => {
    if (item.children.length === 0) return;

    const isOpen = isNodeExpanded(item.id);

    if ((event.key === "ArrowLeft" && isOpen) || (event.key === "ArrowRight" && !isOpen)) {
      event.preventDefault();
      toggleExpand(item.id);
    }
  };

  const isDescendant = (item: IBaseTheme, selectedBranch: IBaseTheme): boolean => {
    return (item.parent_list ?? []).some((p) => p.id === selectedBranch.id);
  };

  const highlightMatch = (title: string): React.ReactNode => {
    // in search mode, highlight the portion of the title matching the query
    if (!searchQuery) return undefined;

    const idx = title.toLowerCase().indexOf(searchQuery);
    if (idx === -1) return undefined;

    return (
      <>
        {title.slice(0, idx)}
        <mark className="rounded-sm bg-background-searchTextHighlight text-gray-950">
          {title.slice(idx, idx + searchQuery.length)}
        </mark>
        {title.slice(idx + searchQuery.length)}
      </>
    );
  };

  useEffect(() => {
    if (!selectedIds?.length) return;

    const selectedParentIds = new Set<string>();
    const expandParents = (items: IOptions, currentParentId: string | null = null) => {
      items.forEach((item) => {
        if (currentParentId && selectedIds.includes(item.id)) {
          selectedParentIds.add(currentParentId);
        }
        if (item.children?.length > 0) {
          expandParents(item.children, item.id);
        }
      });
    };

    expandParents(options);

    // Merge in, don't replace: a re-render handing a new `options` array must not
    // collapse branches the user expanded manually.
    setExpandedIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      selectedParentIds.forEach((id) => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedIds, options]);

  const renderOptions = (items: IThemeOption[], level = 0) => {
    const hasNoItems = !items.length;

    if (hasNoItems && level <= 1) {
      if (searchQuery) {
        return (
          <p
            className={cn("px-4 py-4 text-center text-xs text-[#6B7080]", {
              "flex h-40 items-center justify-center": isSearchMode,
            })}
          >
            No results for &ldquo;{searchQuery}&rdquo;
          </p>
        );
      }

      return (
        <div
          className={cn(
            "inline-flex w-full rounded-md bg-[#F9F9FB] dark:bg-background-primaryDark p-4 text-sm font-medium leading-[1.2] text-gray-450",
            { "flex h-40 items-center justify-center": isSearchMode }
          )}
        >
          No themes found
        </div>
      );
    }

    return (
      <ul className="w-full bg-white dark:bg-background-primaryDark">
        {items.map((item) => {
          const isSelected = selected?.has(item.id) ?? false;
          const isItemLoading =
            loadingThemeIds.has(item.id) ||
            (item.parent_list?.some((p) => loadingThemeIds.has(p.id)) ?? false);
          const isEnabled = selectedBranches.some((branch) => isDescendant(item, branch));
          const isDisabled = isMaxLimitReached && !isSelected && !isEnabled;

          return (
            <li key={item.id} className="cursor-pointer select-none">
              <div
                className={cn(
                  "my-1 flex w-full items-center rounded-md px-1 py-1 hover:bg-background-secondary dark:hover:bg-background-secondaryDark",
                  {
                    "bg-teal-50 dark:bg-background-secondaryDark": isSelected,
                    "hover:bg-transparent": isDisabled,
                  }
                )}
                style={{ paddingLeft: level * 24 + 4 }}
              >
                <button
                  type="button"
                  tabIndex={-1}
                  className={cn(
                    "mr-2 flex h-5 w-5 flex-shrink-0 items-center justify-center text-gray-600 focus:outline-none",
                    { invisible: item.children.length === 0 }
                  )}
                  onClick={(event) => {
                    if (item.children.length === 0) return;

                    event.stopPropagation();
                    toggleExpand(item.id);
                  }}
                >
                  <ChevronDownIcon
                    width={20}
                    height={20}
                    className={cn(
                      "translate-x-1 transition-transform duration-300 dark:text-invertedPrimary80",
                      { "-rotate-90": !isNodeExpanded(item.id) }
                    )}
                  />
                </button>
                <button
                  type="button"
                  className="flex min-w-0 w-full items-center"
                  onClick={(event) => {
                    event.stopPropagation();
                    !isDisabled && onSelect(item);
                  }}
                  onKeyDown={(event) => handleItemKeyDown(event, item)}
                >
                  <div
                    className={cn(
                      "ml-0.5 flex w-full items-center justify-between gap-2 text-sm text-gray-950 dark:text-invertedPrimary80",
                      { "cursor-not-allowed text-content-disabled": isDisabled }
                    )}
                  >
                    <ThemeBadge
                      level={item.level}
                      title={item.title}
                      titleSlot={highlightMatch(item.title)}
                      size="medium"
                      containerClassName="bg-transparent dark:bg-transparent"
                      isDisabled={isDisabled}
                      isLevelOneBased={isLevelOneBased}
                    />

                    <View.Condition if={isItemLoading}>
                      <Spinner size={20} thickness={2} />
                    </View.Condition>
                    <View.Condition if={!isItemLoading && isSelected}>
                      <CheckIcon
                        width={20}
                        height={20}
                        className="text-teal-600 dark:text-teal-400 flex-shrink-0"
                      />
                    </View.Condition>
                  </div>
                </button>
              </div>
              {item.children.length > 0 &&
                isNodeExpanded(item.id) &&
                renderOptions(item.children, level + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={cn("flex max-h-[370px] flex-col overflow-y-auto", className)}>
      <View.Condition if={isMaxLimitReached}>
        <p className="box-border rounded-md border bg-background-secondary dark:bg-gray-600 px-2 py-1.5 text-sm text-gray-450 dark:text-invertedPrimary80">
          Maximum limit reached
        </p>
      </View.Condition>

      {renderOptions(options as IThemeOption[])}
    </div>
  );
};
